import type { GoalPreset, StudentPath } from '@aj/core';
import { presetsFor } from '@aj/core';

import { useAppData } from './AppDataProvider.js';
import { useT } from '../providers/I18nProvider.js';

/**
 * Goals a student can start already broken into parts.
 *
 * DELIBERATELY EMPTY. The shape, the lookup and the UI that offers them are
 * finished and tested; what is missing is the syllabus itself, which is
 * research rather than engineering — the HSC Physics chapter list has to be
 * right, in both languages, or it is worse than nothing. Everything renders
 * correctly with zero packs: `GoalCompose` simply shows no preset row.
 *
 * To fill it, add entries below. Nothing else needs to change.
 *
 *   {
 *     id: 'hsc-physics-1',                 // stable; stored on the goal
 *     path: 'hsc',
 *     category: 'academic',
 *     title: t('preset.hscPhysics1.title'),
 *     parts: [t('preset.hscPhysics1.p1'), …],
 *   }
 *
 * Titles and parts arrive already translated, because `@aj/core` has no
 * `t()`. A pack is therefore built per-locale at read time, which is why this
 * is a function of the dictionary rather than a module-level constant.
 */
function packs(_t: ReturnType<typeof useT>): GoalPreset[] {
  return [];
}

/**
 * The presets offered to this student, in offer order.
 *
 * Keyed by what they said they are preparing for. Someone who skipped the
 * question gets nothing — a list of HSC chapters shown to a person training
 * for a marathon is worse than an empty screen, and the question is one tap
 * away in onboarding for anyone who wants it.
 */
export function useGoalPresets(): GoalPreset[] {
  const { state } = useAppData();
  const t = useT();
  const path: StudentPath | null = state.profilePath;
  if (path === null) return [];
  return presetsFor(packs(t), path);
}
